import React, { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useI18n } from "../../i18n";

/**
 * Public CRA Applicability Checker section.
 *
 * The actual questionnaire, decision engine, tooltips, consent wording,
 * Terms of Use and Free-or-Pay gate are provided by the supplied
 * `cra_applicability_checker_v1_8.html` (source of truth). We embed it
 * as-is via an iframe so the CRA decision logic is not touched.
 *
 * The iframe posts to `/api/cra-checker-lead` and
 * `/api/cra-checker-payment-request` (relative URLs → same origin).
 */
const CRA_CHECKER_URL = "/tools/cra-check.html";

export const CraApplicabilityChecker = () => {
  const { t, lang } = useI18n();
  const [launched, setLaunched] = useState(false);
  const iframeRef = useRef(null);

  // When user toggles the site language, propagate it to the iframe once
  // it is mounted. The iframe listens on `nl:cra-check-lang` events on its
  // own window, dispatched via postMessage.
  useEffect(() => {
    if (!launched) return;
    const send = () => {
      const w = iframeRef.current?.contentWindow;
      if (!w) return;
      try {
        w.postMessage({ type: "nl:cra-check-lang", lang }, window.location.origin);
      } catch (_) {}
    };
    // small delay so iframe finished bootstrapping
    const id = setTimeout(send, 300);
    return () => clearTimeout(id);
  }, [lang, launched]);

  return (
    <section
      id="assessment"
      data-testid="cra-check-section"
      className="relative py-24 md:py-36"
    >
      <div className="absolute inset-0 ambient-glow pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6] mb-4">
            {t.craCheck.overline}
          </div>
          <h2 className="font-display font-extrabold tracking-tight text-3xl md:text-5xl text-white leading-tight">
            {t.craCheck.title}
          </h2>
          <p className="mt-4 text-gray-400 max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            {t.craCheck.sub}
          </p>
        </div>

        {!launched ? (
          <div className="glass rounded-sm p-8 md:p-12 flex flex-col items-center text-center gap-6">
            <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-gray-400">
              {t.craCheck.methodOverline}
            </div>
            <p className="max-w-xl text-gray-300 leading-relaxed">
              {t.craCheck.methodDesc}
            </p>
            <button
              data-testid="cra-check-launch"
              onClick={() => setLaunched(true)}
              className="trace-btn inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] text-white font-medium px-8 py-4 rounded-sm transition-colors duration-200"
            >
              {t.craCheck.start}
              <ArrowRight size={18} />
            </button>
            <p className="text-xs text-gray-400 max-w-md leading-relaxed">
              {t.craCheck.disclaimer}
            </p>
          </div>
        ) : (
          <div
            className="glass rounded-sm overflow-hidden"
            data-testid="cra-check-iframe-wrap"
            style={{ minHeight: "1200px" }}
          >
            <iframe
              ref={iframeRef}
              src={`${CRA_CHECKER_URL}?lang=${lang}`}
              title="CRA Applicability Checker"
              data-testid="cra-check-iframe"
              className="w-full block"
              style={{
                border: "0",
                minHeight: "1200px",
                height: "1200px",
                background: "transparent",
              }}
              // sandbox is intentionally NOT set — the iframe needs same-origin
              // access to POST to /api/*. It is served from our own domain.
              loading="eager"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default CraApplicabilityChecker;
